<script setup lang="ts">
/**
 * AdminAside — правый контекстный aside админ-лейаута (admin-kit).
 * Контент задаётся страницей через useAdminAside().setPanel(...). Десктоп (xl+).
 * Рендер внутри <ClientOnly> — панель ставится на клиенте (onMounted), без SSR-mismatch.
 */
withDefaults(defineProps<{ mobileOpen?: boolean }>(), { mobileOpen: false })
const emit = defineEmits<{ (e: 'close'): void }>()

defineOptions({ name: 'AdminAside' })

const { panel, open, hasCustom } = useAdminAside()
</script>

<template>
  <!-- Aside и его teleport-таргет #admin-aside-slot всегда в DOM (не v-if), иначе при
       закрытии target исчезает из-под активного <Teleport> → краш Vue при unmount.
       Десктоп (xl+): статичная колонка, видимость по `open`.
       Мобайл: off-canvas drawer справа, видимость по `mobileOpen` (transform). -->
  <aside
    data-testid="admin-aside"
    class="fixed xl:static inset-y-0 right-0 z-50 xl:z-auto flex flex-col w-72 max-w-[85vw] shrink-0 bg-hui-content1 xl:rounded-hui-lg overflow-y-auto transition-transform duration-200"
    :class="[
      mobileOpen ? 'translate-x-0' : 'translate-x-full xl:translate-x-0',
      open ? 'xl:flex' : 'xl:hidden',
    ]"
  >
    <!-- Хедер только на мобиле: заголовок + закрыть -->
    <div class="flex items-center justify-between h-14 px-4 shrink-0 border-b border-hui-divider xl:hidden">
      <span class="text-sm font-semibold text-hui-foreground">{{ panel?.title ?? 'Панель' }}</span>
      <button
        type="button"
        class="inline-flex items-center justify-center w-8 h-8 rounded-hui-sm text-hui-foreground/60 hover:bg-hui-default-100 transition-colors outline-none"
        aria-label="Закрыть панель"
        @click="emit('close')"
      >
        <Icon name="heroicons:x-mark" class="w-5 h-5" />
      </button>
    </div>

    <ClientOnly>
      <!-- Точка проекции произвольного контента страницы через <Teleport to="#admin-aside-slot">.
           display:contents — пустой слот не занимает места и не ломает декларативную панель. -->
      <div id="admin-aside-slot" class="contents" />

      <template v-if="panel && !hasCustom">
        <div class="px-4 py-4 border-b border-hui-divider">
          <h3 class="text-sm font-semibold text-hui-foreground">{{ panel.title ?? 'Сводка' }}</h3>
          <p v-if="panel.subtitle" class="text-xs text-hui-foreground/60 mt-0.5">{{ panel.subtitle }}</p>
        </div>

        <div v-if="panel.stats?.length" class="p-4 grid gap-3" :class="panel.statsColumns === 1 ? 'grid-cols-1' : 'grid-cols-2'">
          <div
            v-for="(s, i) in panel.stats"
            :key="i"
            class="rounded-hui-md border border-hui-divider p-3"
          >
            <p class="text-xs text-hui-foreground/60" :class="panel.statsColumns === 1 ? '' : 'truncate'">{{ s.label }}</p>
            <p class="text-xl font-semibold mt-1" :class="s.color ? `text-hui-${s.color}` : 'text-hui-foreground'">
              {{ s.value }}
            </p>
            <p v-if="s.hint" class="text-[0.7rem] text-hui-foreground/50 mt-0.5 truncate">{{ s.hint }}</p>
          </div>
        </div>

        <div v-if="panel.actions?.length" class="px-4 pb-4 mt-auto space-y-2">
          <p class="text-xs font-semibold uppercase tracking-wider text-hui-foreground/40 mb-1">Действия</p>
          <UiKitButton
            v-for="(a, i) in panel.actions"
            :key="i"
            :color="a.color ?? 'default'"
            variant="flat"
            full-width
            :loading="a.loading"
            @click="a.onClick"
          >
            <template v-if="a.icon" #leading>
              <Icon :name="a.icon" class="w-4 h-4" />
            </template>
            {{ a.label }}
          </UiKitButton>
        </div>
      </template>

      <div v-else-if="!hasCustom" class="flex flex-col items-center justify-center h-full gap-2 p-6 text-center">
        <Icon name="heroicons:information-circle" class="w-8 h-8 text-hui-foreground/30" />
        <p class="text-sm text-hui-foreground/50">Контекстная панель появится здесь</p>
      </div>
    </ClientOnly>
  </aside>
</template>
